import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Lock } from 'lucide-react'
import { SimpleIcon } from '@adthlete/common'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import athleteApi from '@/lib/api'
import { afterSocialConnect } from '@/lib/setupSteps'

type Slug = 'instagram' | 'tiktok' | 'youtube'

type Props = {
  slug: Slug
  name: string
  /** Icon background: a CSS background value */
  iconBg: string
  /** What brands get to see once it's connected, as one short line */
  shows: string
  buttonLabel: string
  onConnect: () => void
  connecting: boolean
  /** Called when the athlete comes back to this page (e.g. the browser back button after
   *  leaving for the platform), so the button stops saying "Connecting…" */
  onReturn: () => void
  /** Short requirement under the button, with an optional help link */
  requirement?: { text: string; linkLabel: string; href: string }
  /** Replaces the button with this message (e.g. the platform turned the connection down) */
  unavailable?: string
}

/**
 * One screen to connect a social account: what it is worth, one line on why, and the button.
 * Shared by Instagram, TikTok and YouTube so they look and read the same. During onboarding
 * every way out of it leads back to the Connect accounts step.
 */
const ConnectPlatform: React.FC<Props> = ({
  slug,
  name,
  iconBg,
  shows,
  buttonLabel,
  onConnect,
  connecting,
  onReturn,
  requirement,
  unavailable,
}) => {
  const navigate = useNavigate()
  const { athlete, refreshUser } = useAuth()
  const backTo = afterSocialConnect(athlete)
  const backLabel = 'Settings'

  // Ask first whether this server can connect the platform at all, instead of sending the
  // athlete off to a redirect that can't come back
  const [available, setAvailable] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    athleteApi
      .get('/athlete/oauth/availability')
      .then((res) => !cancelled && setAvailable(res.data?.[slug] !== false))
      .catch(() => !cancelled && setAvailable(true))
    return () => {
      cancelled = true
    }
  }, [slug])

  // Coming back with the browser's back button restores the page exactly as it was left: reset
  // the button and re-read the account, so onboarding state (and the back link) is current
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      onReturn()
      refreshUser()
    }
    window.addEventListener('pageshow', reset)
    return () => window.removeEventListener('pageshow', reset)
  }, [onReturn, refreshUser])

  // Check again on tap, so a page opened before the check finished (or an old tab) never
  // leaves for a sign-in that can't come back
  const [checking, setChecking] = useState(false)
  const connect = async () => {
    setChecking(true)
    try {
      const res = await athleteApi.get('/athlete/oauth/availability')
      if (res.data?.[slug] === false) {
        setAvailable(false)
        return
      }
    } catch {
      // If the check itself fails, let the platform's own sign-in decide
    } finally {
      setChecking(false)
    }
    onConnect()
  }

  const blocked =
    unavailable ??
    (available === false ? `${name} can't be connected here yet. You can add it later.` : null)

  return (
    <div className="mx-auto flex w-full max-w-md flex-col pb-6">
      <button
        type="button"
        onClick={() => navigate(backTo)}
        className="-ml-1 inline-flex items-center gap-1 self-start text-sm text-gray-600 hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>

      <div className="mt-8 flex flex-col items-center text-center">
        <span
          className="flex h-16 w-16 items-center justify-center rounded-2xl"
          style={{ background: iconBg }}
        >
          <SimpleIcon slug={slug} color="ffffff" height={30} width={30} alt={name} />
        </span>
        <h1 className="text-2xl font-bold mt-4 text-foreground">Connect {name}</h1>
        <span className="mt-2 rounded-full bg-[#C0FD72]/15 px-3 py-1 text-sm font-bold text-[#4d7c0f]">
          Verified numbers for brands
        </span>
        <p className="mt-3 max-w-xs text-gray-600">{shows}</p>
      </div>

      <div className="mt-8">
        {blocked ? (
          <>
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-center text-sm text-amber-700">
              {blocked}
            </p>
            <Button
              variant="lime"
              className="mt-4 h-12 w-full text-base"
              onClick={() => navigate(backTo)}
            >
              Back to Settings
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="lime"
              className="h-12 w-full text-base"
              onClick={connect}
              disabled={connecting || checking}
            >
              {connecting ? 'Connecting…' : buttonLabel}
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500">
              <Lock className="h-3.5 w-3.5" /> Read-only. We never post. Disconnect anytime.
            </p>
          </>
        )}
        {requirement && !blocked ? (
          <p className="mt-4 text-center text-xs text-gray-600">
            {requirement.text}{' '}
            <a
              href={requirement.href}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#4d7c0f] underline-offset-2 hover:underline"
            >
              {requirement.linkLabel}
            </a>
          </p>
        ) : null}
        {!blocked ? (
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="mx-auto mt-6 block text-sm text-gray-600 underline-offset-2 hover:text-foreground hover:underline"
          >
            Having trouble? Skip for now
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default ConnectPlatform
